import { useEffect } from 'react'
import type { JSX } from 'react'

import {
  Button,
  CheckIcon,
  Heading,
  Image,
  List,
  Text,
  UpgradeDialog,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Client,
} from '@buffer-mono/tracking-plan'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'

import imageUrl from './api-upgrade.png'

type UpgradePathName = 'key-limit' | 'rate-limit'

const BULLET_POINTS = [
  'Create up to 5 API keys and 5 App Clients',
  'Higher API rate limits across all time windows',
  'More publishing and analytics features',
]

interface ApiUpgradeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  upgradePathName: UpgradePathName
}

const cta = 'publish-settings-api-upgradeDialog-1' as const

export function ApiUpgradeDialog({
  open,
  onOpenChange,
  upgradePathName,
}: ApiUpgradeDialogProps): JSX.Element {
  const organizationId = useOrganizationId()
  const openGlobalModal = useOpenGlobalModal()

  // Tracked via effect (not onOpenChange) because callers control `open` as a
  // prop; Radix only invokes onOpenChange for internal events (escape, outside
  // click, trigger), so a callback-based hook would miss programmatic opens.
  useEffect(() => {
    if (!open) return

    BufferTracker.upgradePathViewed({
      clientName: Client.PublishWeb,
      product: 'publish',
      organizationId: organizationId ?? '',
      upgradePathName,
      cta,
    })
  }, [open, organizationId, upgradePathName])

  const handleCTAClick = (): void => {
    BufferTracker.cTAClicked({
      clientName: Client.PublishWeb,
      product: 'publish',
      cta,
      upgradePathName,
      organizationId: organizationId ?? '',
    })

    onOpenChange(false)
    openGlobalModal(MODALS.planSelector, {
      cta,
      upgradePathName,
      planSelector: {
        initialStep: 'upgrade',
      },
    })
  }

  return (
    <UpgradeDialog open={open} onOpenChange={onOpenChange}>
      <UpgradeDialog.Content size="xlarge">
        <UpgradeDialog.TextContent>
          <Heading size="large">
            Upgrade to do more with Buffer&apos;s API
          </Heading>

          <Text>
            You&apos;ve reached your plan limit for API keys and App Clients.
            Upgrade to unlock more capacity for your workflows and automations.
          </Text>

          <List>
            {BULLET_POINTS.map((item) => (
              <List.Item key={item} icon={CheckIcon}>
                {item}
              </List.Item>
            ))}
          </List>

          <Button variant="primary" size="large" onClick={handleCTAClick}>
            See Upgrade Options
          </Button>
        </UpgradeDialog.TextContent>
        <UpgradeDialog.Image>
          <Image
            alt="illustration showcasing possibility to use multiple API keys"
            src={imageUrl}
          />
        </UpgradeDialog.Image>
      </UpgradeDialog.Content>
    </UpgradeDialog>
  )
}
